(ns hai.journal2.integration-card
  "Card for the Integrations grid on the Connections page. Same shape as
   the connection card (head + status + foot, no toggle) but driven by
   timeline-integration data and the journal2 integration registry.
   Shared scaffolding lives in [[hai.web-app.entity-card]]."
  (:require
   [hai.journal2.events :as events]
   [hai.journal2.integration-registry :as reg]
   [hai.library.connections.invalid-reason :as invalid-reason]
   [hai.ui.components :as c]
   [hai.ui.components.card :as ui-card]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [hai.web-app.entity-card :as entity-card]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Container
;; ---------------------------------------------------------------------------

(o/defstyled integration-card :article
  {:display "grid"
   :grid-auto-rows "min-content"
   :gap "14px"
   :padding "18px"
   :min-height "168px"
   :border-radius "14px"
   :background t/--card-bg
   :border "1px solid var(--card-forest-70)"
   :box-shadow "0 1px 0 var(--card-ink-04), 0 1px 2px var(--card-ink-04)"
   :transition "background .15s ease, border-color .15s ease"
   :position "relative"}
  ;; Bulk-action checkbox lives in `ui-card/edit-overlay` (absolute top-right).
  ;; Default opacity 0 expects the `card-hover` mixin; we don't use it, so
  ;; force the overlay visible.
  [ui-card/edit-overlay {:opacity "1"}])

;; ---------------------------------------------------------------------------
;; Head row — icon + title
;; ---------------------------------------------------------------------------

(o/defstyled head-row :div
  {:display "flex" :align-items "center" :gap "12px"})

(o/defstyled title-text :h3
  {:flex "1" :min-width "0"
   :margin "0"
   :font-size "15px" :font-weight "600" :line-height "1.25"
   :letter-spacing "-0.005em"
   :color t/--card-ink
   :white-space "nowrap" :overflow "hidden" :text-overflow "ellipsis"})

;; ---------------------------------------------------------------------------
;; Status row — dot + label + optional spinner + optional Import action
;; ---------------------------------------------------------------------------

;; Wraps instead of overflowing the card: a long status, the account email and
;; Reconnect / Import history don't fit on one line of a narrow card.
(o/defstyled status-row :div
  {:display "flex" :flex-wrap "wrap" :align-items "center" :gap "6px 8px"})

(o/defstyled status-dot :span
  {:width "6px" :height "6px"
   :flex-shrink "0"
   :border-radius "50%"
   :background t/--card-track}
  [:&.connected        {:background t/--card-forest  :box-shadow "0 0 0 3px var(--card-forest-10)"}]
  [:&.always-available {:background t/--card-forest  :box-shadow "0 0 0 3px var(--card-forest-10)"}]
  [:&.importing        {:background t/--card-info    :box-shadow "0 0 0 3px var(--card-info-12)"}]
  [:&.broken           {:background t/--card-error   :box-shadow "0 0 0 3px var(--card-error-12)"}]
  [:&.disconnected     {:background t/--card-warning :box-shadow "0 0 0 3px var(--card-warning-12)"}])

(o/defstyled status-label :span
  {:font-size "12px" :font-weight "500"
   :color t/--card-text-muted
   :white-space "nowrap"}
  [:&.connected        {:color t/--card-forest}]
  [:&.always-available {:color t/--card-forest}]
  [:&.importing        {:color t/--card-info}]
  [:&.broken           {:color t/--card-error}]
  [:&.disconnected     {:color t/--card-warning-text}])

;; Shrinks to an ellipsis rather than breaking mid-address when the email is
;; wider than the card.
(o/defstyled account-email :span
  {:min-width "0" :max-width "100%"
   :font-size "12px" :color t/--card-text-muted
   :white-space "nowrap" :overflow "hidden" :text-overflow "ellipsis"})

(o/defstyled reconnect-link :a
  {:margin-left "auto"
   :white-space "nowrap"
   :font-size "12px" :font-weight "600"
   :color t/--card-error
   :text-decoration "none"}
  [:&:hover {:text-decoration "underline"}])

(def ^:private import-icon (icons/mui-icon :outlined :file_download))

(o/defstyled import-action :button
  {:margin-left "auto"
   :display "inline-flex" :align-items "center" :gap "4px"
   :height "26px" :padding "0 10px"
   :border-radius "7px"
   :border "none"
   :background "transparent"
   :color t/--card-ink
   :font-family "inherit" :font-size "12px" :font-weight "500"
   :cursor "pointer"
   :transition "background .15s ease"}
  [:&:hover {:background t/--card-violet-soft}]
  [:svg {:width "14px" :height "14px"}])

;; ---------------------------------------------------------------------------
;; Foot row — sharing block on the left
;; ---------------------------------------------------------------------------

(o/defstyled foot-row :div
  {:display "flex" :flex-direction "column" :align-items "stretch"
   :gap "4px"
   :margin-top "10px"})

;; ---------------------------------------------------------------------------
;; Status derivation
;; ---------------------------------------------------------------------------

;; `:broken` has no entry: its wording depends on why the connection broke
;; (see `invalid-reason/describe`).
(def ^:private status-copy
  {:always-available "Always available"
   :importing        "Importing data now"
   :connected        "Connected"
   :disconnected     "Disconnected"})

(defn- status-for
  "`:broken` (connection exists but the platform marked it invalid — see
  `invalid-reason/describe` for what's wrong and whether Reconnect helps)
  is distinguished from `:disconnected` (no connection ref at all — a
  pre-existing orphan from before connections became 1:1 with their TI, see
  `:timeline-integration/connection-status` in hai.pathom.integration) so the
  card can offer the right affordance: reconnect vs. delete."
  [{connected?      :timeline-integration/connected?
    backfilling?    :timeline-integration/backfill-in-progress?
    conn-status     :timeline-integration/connection-status}
   type-ident]
  (let [connectable? (:connectable? (get reg/registry type-ident))]
    (cond
      (not connectable?)        :always-available
      backfilling?               :importing
      connected?                 :connected
      (= conn-status :broken)    :broken
      :else                      :disconnected)))

;; ---------------------------------------------------------------------------
;; Top-level render
;; ---------------------------------------------------------------------------

(defn- reconnect-url [type-ident]
  (reg/oauth-url type-ident nil))

(defn render
  [collection row]
  (let [{:as item} (:form/current-value row)
        type-ident   (get-in item [:timeline-integration/type :db/ident])
        spec         (get reg/registry type-ident)
        label-text   (events/integration-type-label type-ident)
        status       (status-for item type-ident)
        problem      (when (= status :broken)
                       (invalid-reason/describe
                        (:timeline-integration/connection-invalid-reason item)))
        connectable? (:connectable? spec)
        connected?   (:timeline-integration/connected? item)
        backfilling? (:timeline-integration/backfill-in-progress? item)
        email        (:timeline-integration/connection-identity item)
        icon-fn      (:icon-fn spec)
        ;; `currentColor`-fill MUI icons (Tasks, default Calendar) inherit
        ;; this. Brand SVGs with hard-coded fills (Slack, GitHub, Linear,
        ;; Google Calendar, Notes) just ignore it.
        icon-color   (:icon-color spec)]
    [integration-card
     [cards/card-select-checkbox collection row]
     [head-row
      [entity-card/icon-frame
       (when icon-color {:style {:color icon-color}})
       (when icon-fn (icon-fn))]
      [title-text label-text]]
     [status-row
      [status-dot {:class (name status)}]
      [status-label {:class (name status) :title (:explanation problem)}
       (or (:status problem) (status-copy status))]
      (when email [account-email {:title email} (str "(" email ")")])
      (when backfilling? [c/Spinner {:size "1"}])
      (when (and connectable? connected?)
        [import-action
         {:type "button"
          :title "Import history"
          :on-click (fn [e]
                      (.stopPropagation e)
                      (>e [:timeline/open-backfill type-ident]))}
         [import-icon] "Import history"])
      (when (:reconnect? problem)
        [reconnect-link {:href (reconnect-url type-ident) :target "_top"
                         :on-click (fn [e] (.stopPropagation e))}
         "Reconnect"])]
     [foot-row
      [entity-card/sharing-block
       {:collection         collection
        :entity             item
        :aria-label         "Share integration"
        :dialog-description "People with access to this integration"}]]]))
